import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import { Base } from "../../../framework/base";
import { useTranslation } from "react-i18next";
import WorkTimeSlotCard from "./workTimeSlotCard";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import {
    setHoveredWorkShiftTimeSlotId,
    setSelectedWorkShiftTimeSlotId,
    setSidebarTitle,
    fetchEmployeeWorkShiftByDate,
    fetchEmployeeWorkShiftDates,
} from "../../../store/monitorViewSlice";
import CompactDatePicker from "../../framework/compactDatePicker";
import { TransportOrderListItemDto } from "../../../models/transport/transportOrder";
import VehicleOrdersSection from "./fleetOverviewCard/vehicleOrdersSection";

interface MonitorViewWorkShiftsProps {
    transportOrders?: TransportOrderListItemDto[];
}

export const MonitorViewWorkShifts = ({
    transportOrders,
}: MonitorViewWorkShiftsProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const workShift = useAppSelector(
        (state) => state.monitorView.selectedWorkShift
    );
    const workShiftByDateLoading = useAppSelector(
        (state) => state.monitorView.workShiftByDateLoading
    );
    const selectedWorkShiftTimeSlotId = useAppSelector(
        (state) => state.monitorView.selectedWorkShiftTimeSlotId
    );
    const selectedEmployeeId = useAppSelector(
        (state) => state.monitorView.selectedEmployeeId
    );
    const employeeWorkShiftDates = useAppSelector(
        (state) => state.monitorView.employeeWorkShiftDates
    );

    // Keep track of the employee name so it persists across date changes
    const employeeNameRef = useRef<string>("");

    useEffect(() => {
        const name = workShift?.workShiftTimeSlots?.[0]?.employeeName;
        if (name) {
            employeeNameRef.current = name;
        }
        dispatch(setSidebarTitle(employeeNameRef.current));
    }, [workShift, dispatch]);

    const [currentDate, setCurrentDate] = useState<Date>(() => {
        if (workShift?.effectiveDate) {
            return Base.dayjsToMinuteAccuracy(workShift.effectiveDate).toDate();
        }
        return Base.dayjsToMinuteAccuracy().toDate();
    });

    // Fetch work shift dates for the current month when employee changes or month changes
    const [currentMonth, setCurrentMonth] = useState<string>(
        Base.dayjsToMinuteAccuracy(currentDate).startOf("month").toISOString()
    );

    useEffect(() => {
        if (selectedEmployeeId) {
            dispatch(
                fetchEmployeeWorkShiftDates({
                    employeeId: selectedEmployeeId,
                    month: currentMonth,
                }) as any
            );
        }
    }, [selectedEmployeeId, currentMonth, dispatch]);

    const highlightDates = useMemo(
        () => employeeWorkShiftDates.map((d) => Base.dayjsToMinuteAccuracy(d).toDate()),
        [employeeWorkShiftDates]
    );

    const handleDateChange = useCallback(
        (val: Date | null) => {
            if (val && selectedEmployeeId) {
                setCurrentDate(val);
                const newMonth = Base.dayjsToMinuteAccuracy(val).startOf("month").toISOString();
                if (newMonth !== currentMonth) {
                    setCurrentMonth(newMonth);
                }
                dispatch(
                    fetchEmployeeWorkShiftByDate({
                        employeeId: selectedEmployeeId,
                        date: Base.dayjsToMinuteAccuracy(val).toISOString(),
                    }) as any
                );
            }
        },
        [selectedEmployeeId, currentMonth, dispatch]
    );

    const handleMonthChange = useCallback((date: Date) => {
        setCurrentMonth(Base.dayjsToMinuteAccuracy(date).startOf("month").toISOString());
    }, []);

    // Update currentDate when workShift changes (e.g. initial load)
    useEffect(() => {
        if (workShift?.effectiveDate) {
            const newDate = Base.dayjsToMinuteAccuracy(workShift.effectiveDate).toDate();
            setCurrentDate(newDate);
            const newMonth = Base.dayjsToMinuteAccuracy(newDate).startOf("month").toISOString();
            if (newMonth !== currentMonth) {
                setCurrentMonth(newMonth);
            }
        }
    }, [workShift?.effectiveDate]);

    const { workShiftTimeSlots } = workShift || {};

    const shiftTimeLabel = useMemo(() => {
        if (!workShift) return "";
        const date = Base.dayjsToDateStr(workShift.startTime);
        const start = Base.dateStrToOriginalTimezoneTimeStr(
            workShift.startTime
        );
        const end = workShift.endTime
            ? Base.dateStrToOriginalTimezoneTimeStr(workShift.endTime)
            : "";
        return `${date} ${start} - ${end}`;
    }, [workShift]);

    // Get orders for vehicles associated with this employee's work shift
    // Only include orders whose scheduled period overlaps the selected date
    const employeeOrders = useMemo(() => {
        if (!transportOrders?.length || !workShiftTimeSlots?.length) return [];
        const vehicleIds = new Set(
            workShiftTimeSlots
                .map((s) => s.vehicleId?.toLowerCase())
                .filter(Boolean)
        );
        const dayStart = Base.dayjsToMinuteAccuracy(currentDate).startOf("day");
        const dayEnd = Base.dayjsToMinuteAccuracy(currentDate).endOf("day");
        return transportOrders.filter((o) => {
            if (!o.vehicleId || !vehicleIds.has(o.vehicleId.toLowerCase()))
                return false;
            const start = o.scheduledStartDateTime
                ? Base.dayjsToMinuteAccuracy(o.scheduledStartDateTime)
                : null;
            const end = o.scheduledEndDateTime
                ? Base.dayjsToMinuteAccuracy(o.scheduledEndDateTime)
                : null;
            if (start && start.isAfter(dayEnd)) return false;
            if (end && end.isBefore(dayStart)) return false;
            return true;
        });
    }, [transportOrders, workShiftTimeSlots, currentDate]);

    const datePicker = selectedEmployeeId ? (
        <CompactDatePicker
            value={currentDate}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            highlightDates={highlightDates}
            navigateThroughHighlightedDates
            displayLabel={shiftTimeLabel || undefined}
            sx={{ margin: 0.5 }}
        />
    ) : null;

    if (!workShift && !workShiftByDateLoading)
        return (
            <>
                {datePicker}
                <Alert variant="standard" severity="info">
                    {t("monitorView.workTime.noShiftForDate")}
                </Alert>
            </>
        );

    return (
        <>
            {datePicker}
            {workShiftByDateLoading ? (
                <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                </Box>
            ) : (
                <>
                    {employeeOrders.length > 0 && (
                        <Box sx={{ px: 0.5, pt: 0.5 }}>
                            <VehicleOrdersSection orders={employeeOrders} />
                            <Divider sx={{ mt: 0.5 }} />
                        </Box>
                    )}
                    <Box sx={{ px: 0.5, pt: 0.5 }}>
                        {(() => {
                            const sorted = workShiftTimeSlots
                                ?.slice()
                                .sort((a, b) =>
                                    Base.dayjsCompare(a.startDate, b.startDate)
                                );
                            return sorted?.map((s, index) => (
                                <React.Fragment key={s.id}>
                                    {index > 0 &&
                                        sorted[index - 1].vehicleId !==
                                            s.vehicleId && (
                                            <Divider
                                                sx={{
                                                    borderWidth: "2px",
                                                    my: 0.5,
                                                }}
                                            />
                                    )}
                                    <WorkTimeSlotCard
                                        slot={s}
                                        isSelected={
                                            s.id === selectedWorkShiftTimeSlotId
                                        }
                                        onSelect={(id) =>
                                            dispatch(
                                                setSelectedWorkShiftTimeSlotId(
                                                    id
                                                )
                                            )
                                        }
                                        onHover={(id) =>
                                            dispatch(
                                                setHoveredWorkShiftTimeSlotId(
                                                    id
                                                )
                                            )
                                        }
                                    />
                                </React.Fragment>
                            ));
                        })()}
                    </Box>
                </>
            )}
        </>
    );
};
